import React, { useState } from "react";
import { useMatter } from "../../../contexts/MatterContext";
import { Field, Fieldset, Label } from "../../../design/fieldset";
import { Heading, Subheading } from "../../../design/heading";
import { Input } from "../../../design/input";
import { Button } from "../../../design/button";
import { CourtDetails } from "../../../models/CourtDetails";
import { Divider } from "../../../design/divider";

interface CourtDetailsSectionProps {}

const CourtDetailsSection: React.FC<CourtDetailsSectionProps> = ({}) => {
  const { updateCourtDetails, currentMatter } = useMatter();
  const [courtDetails, setCourtDetails] = useState<CourtDetails | undefined>(
    currentMatter?.courtDetails,
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (field: keyof CourtDetails, value: string) => {
    setCourtDetails({
      ...courtDetails,
      [field]: value,
    });
  };

  const handleSubmit = async () => {
    if (!courtDetails) return;

    setIsSaving(true);
    try {
      await updateCourtDetails(currentMatter?._id!, courtDetails);
    } catch (error) {
      console.error("Error updating court details:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <Heading>Court Details</Heading>
        <Button
          onClick={handleSubmit}
          disabled={
            isSaving ||
            JSON.stringify(courtDetails) ===
              JSON.stringify(currentMatter?.courtDetails)
          }
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Section 1: Case Info */}
      <Fieldset>
        <Subheading>Case Info</Subheading>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label>Date of Action</Label>
            <Input
              type="date"
              value={
                courtDetails?.dateOfAction
                  ? new Date(courtDetails.dateOfAction)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={(e) => handleChange("dateOfAction", e.target.value)}
            />
          </Field>

          <Field>
            <Label>Case Number</Label>
            <Input
              type="text"
              value={courtDetails?.caseNumber ?? ""}
              onChange={(e) => handleChange("caseNumber", e.target.value)}
              placeholder="Enter case number"
            />
          </Field>
        </div>
      </Fieldset>

      <Divider />

      {/* Section 2: Court Info */}
      <Fieldset>
        <Subheading>Court Info</Subheading>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label>Name of Court</Label>
            <Input
              type="text"
              value={courtDetails?.nameOfCourt ?? ""}
              onChange={(e) => handleChange("nameOfCourt", e.target.value)}
              placeholder="Enter name of court"
            />
          </Field>

          <Field>
            <Label>Country of Court</Label>
            <Input
              type="text"
              value={courtDetails?.countryOfCourt ?? ""}
              onChange={(e) => handleChange("countryOfCourt", e.target.value)}
              placeholder="Enter country"
            />
          </Field>

          <Field>
            <Label>State of Court</Label>
            <Input
              type="text"
              value={courtDetails?.stateOfCourt ?? ""}
              onChange={(e) => handleChange("stateOfCourt", e.target.value)}
              placeholder="Enter state"
            />
          </Field>

          <Field>
            <Label>County of Court</Label>
            <Input
              type="text"
              value={courtDetails?.countyOfCourt ?? ""}
              onChange={(e) => handleChange("countyOfCourt", e.target.value)}
              placeholder="Enter county"
            />
          </Field>

          <Field className="md:col-span-2">
            <Label>Jurisdiction/District</Label>
            <Input
              type="text"
              value={courtDetails?.jurisdictionDistrict ?? ""}
              onChange={(e) =>
                handleChange("jurisdictionDistrict", e.target.value)
              }
              placeholder="Enter jurisdiction or district"
            />
          </Field>

          <Field>
            <Label>Courtroom</Label>
            <Input
              type="text"
              value={courtDetails?.courtroom ?? ""}
              onChange={(e) => handleChange("courtroom", e.target.value)}
              placeholder="Enter courtroom"
            />
          </Field>
        </div>
      </Fieldset>

      <Divider />

      {/* Section 3: People Involved */}
      <Fieldset>
        <Subheading>People Involved</Subheading>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label>Judge</Label>
            <Input
              type="text"
              value={courtDetails?.judge ?? ""}
              onChange={(e) => handleChange("judge", e.target.value)}
              placeholder="Enter judge name"
            />
          </Field>

          <Field>
            <Label>Commissioner</Label>
            <Input
              type="text"
              value={courtDetails?.commissioner ?? ""}
              onChange={(e) => handleChange("commissioner", e.target.value)}
              placeholder="Enter commissioner name"
            />
          </Field>

          <Field>
            <Label>Clerk</Label>
            <Input
              type="text"
              value={courtDetails?.clerk ?? ""}
              onChange={(e) => handleChange("clerk", e.target.value)}
              placeholder="Enter clerk name"
            />
          </Field>

          <Field>
            <Label>Clerk Email</Label>
            <Input
              type="email"
              value={courtDetails?.clerkEmail ?? ""}
              onChange={(e) => handleChange("clerkEmail", e.target.value)}
              placeholder="Enter clerk email"
            />
          </Field>

          <Field>
            <Label>Clerk Phone/Fax</Label>
            <Input
              type="text"
              value={courtDetails?.clerkPhoneFax ?? ""}
              onChange={(e) => handleChange("clerkPhoneFax", e.target.value)}
              placeholder="Enter clerk phone/fax"
            />
          </Field>
        </div>
      </Fieldset>
    </div>
  );
};

export default CourtDetailsSection;
